import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import AppDateField from './AppDateField';
import VisitTimeField from './VisitTimeField';
import { formatTime12hFrom24, parseStoredDatetimeParts, ymdToLocalDate } from '../utils/easternTime';
import { parseFlexibleTimeInput } from '../utils/visitTime';

export interface AppDateTimeFieldProps {
  dateLabel: string;
  timeLabel: string;
  /** `YYYY-MM-DDTHH:mm`, or empty when date or time is missing. */
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  size?: 'small' | 'medium';
  minDateTime?: string | null;
  maxDateTime?: string | null;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function splitValue(value: string): { date: string; time: string } {
  const parts = parseStoredDatetimeParts(value);
  if (!parts) return { date: '', time: '' };
  return {
    date: `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`,
    time: formatTime12hFrom24(parts.hour, parts.minute),
  };
}

function combine(date: string, time: string): string {
  const parsed = parseFlexibleTimeInput(time);
  if (!date || !parsed) return '';
  return `${date}T${pad(parsed.h)}:${pad(parsed.m)}`;
}

const AppDateTimeField: React.FC<AppDateTimeFieldProps> = ({
  dateLabel,
  timeLabel,
  value,
  onChange,
  error,
  helperText,
  required,
  size = 'small',
  minDateTime,
  maxDateTime,
}) => {
  const [date, setDate] = useState(() => splitValue(value).date);
  const [time, setTime] = useState(() => splitValue(value).time);
  const emitted = useRef(value);

  useEffect(() => {
    if (value === emitted.current) return;
    emitted.current = value;
    const next = splitValue(value);
    setDate(next.date);
    setTime(next.time);
  }, [value]);

  const update = (nextDate: string, nextTime: string) => {
    setDate(nextDate);
    setTime(nextTime);
    const combined = combine(nextDate, nextTime);
    emitted.current = combined;
    if (combined !== value) onChange(combined);
  };

  const message = helperText?.trim() ? helperText : undefined;

  return (
    <Box>
      <AppDateField
        label={dateLabel}
        value={date || null}
        onChange={next => update(next ?? '', time)}
        size={size}
        required={required}
        error={error}
        minDate={minDateTime ? ymdToLocalDate(minDateTime) : undefined}
        maxDate={maxDateTime ? ymdToLocalDate(maxDateTime) : undefined}
      />
      <Box sx={{ mt: 1.5 }}>
        <VisitTimeField
          label={timeLabel}
          visitDate={date}
          value={time}
          onChange={next => update(date, next)}
          size={size}
          error={error}
          errorText={error ? message : undefined}
          helperText={error ? undefined : message}
          showVisitLabel={false}
        />
      </Box>
    </Box>
  );
};

export default AppDateTimeField;
